import { useLayoutEffect, useRef, useState, type MouseEvent } from 'react'

/**
 * Graficul "Alarms last 7 days" din alarms/summary.php, desenat ca SVG cu
 * TEMA IMPLICITA a lui Highcharts 4.0.1 (public/plugins/highcharts): aceeasi
 * culoare de serie (#7cb5ec, umplere 0.75), aceleasi axe (linia si liniutele
 * gri-albastrui pe X, gridul #D8D8D8 pe Y, etichete 11px #606060, titlul axei
 * Y la 270 de grade), aceeasi bula la trecerea peste un punct.
 *
 * De ce nu Highcharts insusi: versiunea din portal (4.0.1, 2014) ruleaza doar
 * peste jQuery, iar aplicatia noua nu are jQuery - regula din
 * docs/decizie-v4-rescriere.md. Un grafic de o serie si sapte puncte nu are
 * nevoie de o biblioteca; are nevoie sa arate la fel.
 */

export interface AreaChartProps {
  categories: string[]
  data: number[]
  yTitle: string
  height: number
}

export const SERIES_COLOR = '#7cb5ec'
const FONT = '"Lucida Grande", "Lucida Sans Unicode", Verdana, Arial, Helvetica, sans-serif'
const SPACING = { top: 10, right: 10, bottom: 15, left: 10 }
const X_LABELS_HEIGHT = 25
const Y_TICK_PIXELS = 72

/** normalizeTickInterval din Highcharts, cu allowDecimals: false - pasul e 1, 2, 5 sau 10 ori o putere a lui zece, dar nu sub 1. */
export function tickInterval(range: number, pixels: number): number {
  const raw = Math.max(1, (range * Y_TICK_PIXELS) / Math.max(1, pixels))
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)))
  const normalized = raw / magnitude
  const multiples = [1, 2, 5, 10]
  let chosen = multiples[multiples.length - 1]
  for (let i = 0; i < multiples.length; i++) {
    const next = multiples[i + 1] ?? multiples[i]
    if (normalized <= (multiples[i] + next) / 2) {
      chosen = multiples[i]
      break
    }
  }
  return Math.max(1, chosen * magnitude)
}

/** Capetele si pozitiile axei Y: de la 0 (startOnTick, threshold 0) pana peste maxim cu 5% (maxPadding), rotunjit la pas (endOnTick). */
export function yTicks(data: number[], plotHeight: number): number[] {
  const dataMax = data.length ? Math.max(0, ...data) : 0
  const padded = dataMax === 0 ? 1 : dataMax * 1.05
  const step = tickInterval(padded, plotHeight)
  const max = Math.max(step, Math.ceil(padded / step) * step)
  const ticks: number[] = []
  for (let v = 0; v <= max; v += step) ticks.push(v)
  return ticks
}

let measurer: CanvasRenderingContext2D | null | undefined
function textWidth(text: string, size: number): number {
  if (measurer === undefined) {
    measurer = null
    try {
      /* jsdom nu are canvas; acolo ramane estimarea pe litera. */
      if (typeof document !== 'undefined' && !navigator.userAgent.includes('jsdom')) {
        measurer = document.createElement('canvas').getContext('2d')
      }
    } catch {
      measurer = null
    }
  }
  if (measurer) {
    measurer.font = `${size}px ${FONT}`
    const width = measurer.measureText(text).width
    if (width > 0) return width
  }
  return text.length * size * 0.56
}

export function AreaChart({ categories, data, yTitle, height }: AreaChartProps) {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [hover, setHover] = useState<number | null>(null)

  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const measure = () => setWidth(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const empty = data.length === 0
  const plotTop = SPACING.top
  const plotBottom = height - SPACING.bottom - X_LABELS_HEIGHT
  const plotHeight = Math.max(1, plotBottom - plotTop)
  const ticks = yTicks(data, plotHeight)
  const yMax = ticks[ticks.length - 1]
  const labelWidth = Math.max(...ticks.map((t) => textWidth(String(t), 11)))
  const plotLeft = SPACING.left + 13 + 10 + labelWidth + 8
  const plotRight = width - SPACING.right
  const plotWidth = Math.max(1, plotRight - plotLeft)

  const n = categories.length
  const x = (i: number) => plotLeft + ((i + 0.5) / n) * plotWidth
  const y = (v: number) => plotBottom - (v / yMax) * plotHeight
  const line = data.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(2)} ${y(v).toFixed(2)}`).join(' ')
  const area = data.length ? `${line} L${x(n - 1).toFixed(2)} ${plotBottom} L${x(0).toFixed(2)} ${plotBottom} Z` : ''

  const onMove = (event: MouseEvent<SVGSVGElement>) => {
    if (empty) return
    const rect = event.currentTarget.getBoundingClientRect()
    const px = event.clientX - rect.left
    let best = 0
    for (let i = 1; i < n; i++) if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i
    setHover(best)
  }

  const tip = hover === null ? null : { x: x(hover), y: y(data[hover]), label: categories[hover], value: data[hover] }
  const tipWidth = tip ? Math.max(textWidth(tip.label, 10), textWidth(`Series 1: ${tip.value}`, 12) + 14) + 16 : 0
  const tipHeight = 42
  const tipLeft = tip ? Math.min(Math.max(plotLeft, tip.x - tipWidth / 2), plotRight - tipWidth) : 0
  const tipTop = tip ? Math.max(0, tip.y - tipHeight - 12) : 0

  return (
    <div ref={box} className="highcharts-container" style={{ position: 'relative', overflow: 'hidden', width: '100%', height, textAlign: 'left', lineHeight: 'normal', zIndex: 0, fontFamily: FONT, fontSize: 12 }}>
      {width > 0 && (
        <svg width={width} height={height} style={{ fontFamily: FONT, fontSize: 12 }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
          <rect x={0} y={0} width={width} height={height} fill="#FFFFFF" />
          {!empty && (
            <g className="highcharts-grid">
              {ticks.map((t) => (
                <path key={t} d={`M${plotLeft} ${y(t).toFixed(2)} L${plotRight} ${y(t).toFixed(2)}`} stroke="#D8D8D8" strokeWidth={1} shapeRendering="crispEdges" />
              ))}
            </g>
          )}
          {!empty && (
            <g className="highcharts-axis">
              <path d={`M${plotLeft} ${plotBottom + 0.5} L${plotRight} ${plotBottom + 0.5}`} stroke="#C0D0E0" strokeWidth={1} shapeRendering="crispEdges" />
              {Array.from({ length: n + 1 }, (_, i) => plotLeft + (i / n) * plotWidth).map((tx, i) => (
                <path key={i} d={`M${tx.toFixed(2)} ${plotBottom} L${tx.toFixed(2)} ${plotBottom + 10}`} stroke="#C0D0E0" strokeWidth={1} shapeRendering="crispEdges" />
              ))}
              <text
                transform={`translate(${SPACING.left + 12}, ${plotTop + plotHeight / 2}) rotate(270)`}
                textAnchor="middle"
                style={{ fill: '#707070', fontSize: 12, fontFamily: FONT }}
              >
                {yTitle}
              </text>
            </g>
          )}
          {!empty && (
            <g className="highcharts-series">
              <path d={area} fill={SERIES_COLOR} fillOpacity={0.75} />
              <path d={line} fill="none" stroke={SERIES_COLOR} strokeWidth={hover === null ? 2 : 3} strokeLinejoin="round" strokeLinecap="round" />
              {tip && <circle cx={tip.x} cy={tip.y} r={4} fill={SERIES_COLOR} stroke="#FFFFFF" strokeWidth={1} />}
            </g>
          )}
          {!empty && (
            <g className="highcharts-axis-labels">
              {categories.map((c, i) => (
                <text key={i} x={x(i)} y={plotBottom + 15} textAnchor="middle" style={{ fill: '#606060', fontSize: 11, fontFamily: FONT }}>
                  {c}
                </text>
              ))}
              {ticks.map((t) => (
                <text key={t} x={plotLeft - 8} y={y(t) + 3} textAnchor="end" style={{ fill: '#606060', fontSize: 11, fontFamily: FONT }}>
                  {t}
                </text>
              ))}
            </g>
          )}
          {tip && (
            <g className="highcharts-tooltip" style={{ pointerEvents: 'none' }}>
              <rect x={tipLeft + 1} y={tipTop + 2} width={tipWidth} height={tipHeight} rx={3} fill="#000" opacity={0.05} />
              <rect x={tipLeft} y={tipTop} width={tipWidth} height={tipHeight} rx={3} fill="rgba(249, 249, 249, 0.85)" stroke={SERIES_COLOR} strokeWidth={1} />
              <text x={tipLeft + 8} y={tipTop + 8 + 10} style={{ fill: '#333333', fontSize: 10, fontFamily: FONT }}>
                {tip.label}
              </text>
              <text x={tipLeft + 8} y={tipTop + 8 + 10 + 16} style={{ fill: '#333333', fontSize: 12, fontFamily: FONT }}>
                <tspan style={{ fill: SERIES_COLOR }}>●</tspan> Series 1: <tspan style={{ fontWeight: 'bold' }}>{tip.value}</tspan>
              </text>
            </g>
          )}
        </svg>
      )}
    </div>
  )
}
